/**
 * The browser's calls to `/api/view-as/*` (M4S-5670,
 * docs/pdlc/cs-view-as-client.md §4 and §8).
 *
 * Every call reports a typed result rather than throwing, because the
 * lifecycle has to tell three outcomes apart: the server passed it, the server
 * refused it (end View-as), or the network failed (tolerated for a while).
 *
 * These URLs are same-origin `/api/view-as/*`, which the fetch backstop passes
 * untouched, so recording a write never recurses into the backstop.
 */

import type {
    ViewAsAction,
    ViewAsClient,
    ViewAsClientSearchResponse,
    ViewAsErrorBody,
    ViewAsEventRequest,
    ViewAsSessionResponse,
} from '@/lib/view-as/types';

export type ViewAsCallResult<T> =
    | { ok: true; data: T }
    | { ok: false; kind: 'refused'; status: number; code: string; message: string }
    | { ok: false; kind: 'network'; message: string };

const EVENTS_URL = '/api/view-as/events';

/** Whether a refusal code is one of Studio's own View-as session checks (§4 step 5). */
export function isViewAsSessionCode(code: string): boolean {
    return code.startsWith('view_as_');
}

async function call<T>(
    url: string,
    init: RequestInit & { timeoutMs?: number },
): Promise<ViewAsCallResult<T>> {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    const timer =
        controller && init.timeoutMs
            ? setTimeout(() => controller.abort(), init.timeoutMs)
            : undefined;
    let res: Response;
    try {
        res = await fetch(url, {
            ...init,
            credentials: 'same-origin',
            cache: 'no-store',
            signal: controller?.signal,
        });
    } catch (error) {
        return {
            ok: false,
            kind: 'network',
            message: error instanceof Error ? error.message : String(error),
        };
    } finally {
        if (timer) clearTimeout(timer);
    }

    let body: unknown = null;
    try {
        body = await res.json();
    } catch {
        body = null;
    }
    if (res.ok) return { ok: true, data: body as T };

    const err = (body ?? {}) as Partial<ViewAsErrorBody>;
    // A gateway error with no body is the network's, not a View-as refusal.
    if (!err.error && res.status >= 500) {
        return { ok: false, kind: 'network', message: `HTTP ${res.status}` };
    }
    return {
        ok: false,
        kind: 'refused',
        status: res.status,
        code: typeof err.error === 'string' ? err.error : `http_${res.status}`,
        message: typeof err.message === 'string' ? err.message : `HTTP ${res.status}`,
    };
}

export function postViewAsEvent<T = ViewAsSessionResponse>(
    body: ViewAsEventRequest,
    options: { keepalive?: boolean; timeoutMs?: number } = {},
): Promise<ViewAsCallResult<T>> {
    return call<T>(EVENTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: options.keepalive,
        timeoutMs: options.timeoutMs,
    });
}

/** Read-only client search (name, SF account id or Studio org id). */
export async function searchViewAsClients(query: string): Promise<ViewAsClient[]> {
    const result = await call<ViewAsClientSearchResponse>(
        `/api/view-as/clients?q=${encodeURIComponent(query.trim())}`,
        { method: 'GET' },
    );
    if (!result.ok) {
        throw new Error(result.kind === 'refused' ? result.message : 'Client search failed');
    }
    return result.data.clients ?? [];
}

/** The page path recorded on a row: path only, no query string or hash. */
export function currentPagePath(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    return window.location.pathname.slice(0, 512);
}

/** Fire-and-forget action row (fetch keepalive). Fail-open: a lost row never blocks the action. */
export function sendViewAsAction(
    sessionId: string,
    action: ViewAsAction,
    ref: string | null | undefined,
    detail?: Record<string, unknown>,
): Promise<ViewAsCallResult<{ ok: boolean; recorded?: boolean }>> {
    return postViewAsEvent<{ ok: boolean; recorded?: boolean }>(
        {
            event: 'action',
            sessionId,
            action,
            ref: ref ? String(ref).slice(0, 128) : undefined,
            detail,
            pagePath: currentPagePath(),
        },
        { keepalive: true },
    );
}

/** Fire-and-forget refused row from a browser backstop. */
export function sendViewAsRefusal(
    sessionId: string,
    detail: Record<string, unknown>,
): Promise<ViewAsCallResult<{ ok: boolean }>> {
    return postViewAsEvent<{ ok: boolean }>(
        { event: 'refused', sessionId, detail, pagePath: currentPagePath() },
        { keepalive: true },
    );
}
